import { Localization } from '@aa-app-localization'

export type ProductFilterOperator = 'EQ' | 'NEQ' | 'IN' | 'GTE' | 'LTE' | 'RANGE'

export type ProductFilterInput = {
    attribute: string
    operator: ProductFilterOperator
    values: string[]
}

export type ProductSortInput = {
    attribute: string
    direction: 'asc' | 'desc'
}

export type ProductSearchImage = {
    url?: string | null
    alt?: string | null
}

export type ProductSearchColor = {
    name: string
    presentation?: string | null
}

export type ProductSearchDimensions = {
    width?: number | null
    height?: number | null
    length?: number | null
    diameter?: number | null
    weight?: number | null
}

export type ProductSearchHitSchema = 'legacy' | 'full'

export type ProductSearchHit = {
    ean: string
    objectID?: string
    slug?: string | null
    title?: string | null
    shortDescription?: string | null
    brand?: string | null
    subBrand?: string | null
    range?: string | null
    codeLocal?: string | null
    status?: string | null
    statusOriginal?: string | null
    suggestedRetailPriceNlInCents?: number | null
    suggestedRetailPriceIseInCents?: number | null
    suggestedRetailPriceNlInFloat?: number | null
    suggestedRetailPriceIseInFloat?: number | null
    suggestedStandardRetailPriceNlInCents?: number | null
    suggestedStandardRetailPriceIseInCents?: number | null
    suggestedStandardRetailPriceNlInFloat?: number | null
    suggestedStandardRetailPriceIseInFloat?: number | null
    primaryImage?: ProductSearchImage | null
    primaryImageUrl?: string | null
    price?: number | null
    ambianceImage?: ProductSearchImage | null
    wideImage?: ProductSearchImage | null
    categories?: string[]
    materials?: string[]
    subMaterials?: string[]
    colors?: ProductSearchColor[] | string[]
    subColors?: string[]
    dimensions?: ProductSearchDimensions | null
    deliveryTimeInfo?: string | null
    deliveryTimeInWeeks?: number | null
    orderStep?: number | null
    numberOfIndividuals?: number | null
    seatingComfort?: string | null
    shape?: string | null
    pattern?: string | null
    isNew?: boolean | null
    isSale?: boolean | null
    isFsc?: boolean | null
    is3DModelAvailable?: boolean | null
    isAssemblyRequired?: boolean | null
}

export type FacetDistribution = {
    attribute: string
    values: Array<{ value: string; count: number }>
}

export type FacetStat = {
    attribute: string
    min?: number | null
    max?: number | null
}

export type ProductSearchResult = {
    hits: ProductSearchHit[]
    totalHits: number
    page: number
    perPage: number
    totalPages: number
    processingTimeMs: number
    facets: FacetDistribution[]
    facetStats: FacetStat[]
    appliedSemanticRatio?: number | null
    appliedFilters: string[]
}

export type ProductSearchInput = {
    query?: string
    locale: Localization.Locale
    page?: number
    perPage?: number
    semanticRatio?: number
    sort?: ProductSortInput[]
    filters?: ProductFilterInput[]
    facets?: string[]
}

export const PRODUCT_SEARCH_HIT_FIELDS_LEGACY = `
      ean slug title shortDescription brand status price primaryImageUrl
      categories colors isNew isSale`

const IMAGE_FIELDS = '{ url alt }'

export const PRODUCT_SEARCH_HIT_FIELDS = `
      ean objectID slug title shortDescription brand subBrand range codeLocal
      status statusOriginal
      suggestedRetailPriceNlInCents suggestedRetailPriceIseInCents
      suggestedRetailPriceNlInFloat suggestedRetailPriceIseInFloat
      suggestedStandardRetailPriceNlInCents suggestedStandardRetailPriceIseInCents
      suggestedStandardRetailPriceNlInFloat suggestedStandardRetailPriceIseInFloat
      primaryImage ${IMAGE_FIELDS}
      ambianceImage ${IMAGE_FIELDS}
      wideImage ${IMAGE_FIELDS}
      categories materials subMaterials
      colors { name presentation }
      subColors
      dimensions { width height length diameter weight }
      deliveryTimeInfo deliveryTimeInWeeks orderStep numberOfIndividuals
      seatingComfort shape pattern
      isNew isSale isFsc is3DModelAvailable isAssemblyRequired`

export function resolveProductSearchHitFields(schema: ProductSearchHitSchema = 'legacy'): string {
    return schema === 'full' ? PRODUCT_SEARCH_HIT_FIELDS : PRODUCT_SEARCH_HIT_FIELDS_LEGACY
}

export function buildProductSearchQuery(schema: ProductSearchHitSchema = 'legacy'): string {
    const hitFields = resolveProductSearchHitFields(schema)

    return `
query ProductSearch($input: ProductSearchInput!) {
  productSearch(input: $input) {
    hits {
${hitFields}
    }
    totalHits page perPage totalPages processingTimeMs
    facets { attribute values { value count } }
    facetStats { attribute min max }
    appliedSemanticRatio appliedFilters
  }
}`
}

export function buildProductRecommendationsQuery(schema: ProductSearchHitSchema = 'legacy'): string {
    const hitFields = resolveProductSearchHitFields(schema)

    return `
query ProductRecommendations($ean: String!, $locale: SearchLocale!, $limit: Int) {
  productRecommendations(ean: $ean, locale: $locale, limit: $limit) {
${hitFields}
  }
}`
}

export const PRODUCT_SEARCH_QUERY = buildProductSearchQuery('full')

export const PRODUCT_RECOMMENDATIONS_QUERY = buildProductRecommendationsQuery('full')
